import { BaseError } from '../base';

export class APIError extends BaseError {
  status: number;

  /**
   * The `payload` object from the API error response body, when present.
   *
   * Some error responses carry structured context beyond the `error`/`code`
   * pair (e.g. `merge_accounts_confirmation` returns the field-level merge
   * conflicts under `payload.mergeConflicts`). It is preserved here so that
   * downstream error mappers can surface that context as typed errors instead
   * of discarding it.
   */
  payload: Record<string, unknown> | undefined;

  // eslint-disable-next-line custom-rules/require-single-object-param
  constructor(
    message: string,
    code: string,
    status: number,
    payload?: Record<string, unknown>
  ) {
    super({
      cause: null,
      code,
      docsUrl: null,
      name: 'APIError',
      shortMessage: message,
    });

    this.status = status;
    this.payload = payload;
  }

  static async fromResponse(response: Response) {
    try {
      const errorBody = await response.clone().json();

      if (
        errorBody &&
        'error' in errorBody &&
        typeof errorBody.error === 'string'
      ) {
        const errorCode =
          'code' in errorBody && typeof errorBody.code === 'string'
            ? errorBody.code
            : 'unknown_error';

        const payload =
          'payload' in errorBody &&
          typeof errorBody.payload === 'object' &&
          errorBody.payload !== null
            ? (errorBody.payload as Record<string, unknown>)
            : undefined;

        return new APIError(
          errorBody.error,
          errorCode,
          response.status,
          payload
        );
      }

      return null;
    } catch {
      return null;
    }
  }
}
